import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TripFilter } from "@/screens/dashboard/shortcuts";
import { useTrips } from "@/api/trips";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TripFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tripFilter: TripFilter;
  onFilterChange: (filter: TripFilter) => void;
}

export function TripFilterDialog({
  isOpen,
  onClose,
  tripFilter,
  onFilterChange,
}: TripFilterDialogProps) {
  // Store local trip filter state that will be applied on save
  const [localTripFilter, setLocalTripFilter] =
    useState<TripFilter>(tripFilter);

  // Reset local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalTripFilter(tripFilter);
    }
  }, [isOpen, tripFilter]);

  // Fetch trips for the list
  const { data: tripsResponse } = useTrips({
    status: "PLANNED,ACTIVE,COMPLETED",
  });
  const trips = tripsResponse?.data ?? [];

  // Handlers for local filter state changes
  const toggleShowAll = () => {
    setLocalTripFilter((prev) => ({
      ...prev,
      showAll: !prev.showAll,
    }));
  };

  const toggleTripVisibility = (tripId: string) => {
    if (localTripFilter.hiddenTripIds.includes(tripId)) {
      // Trip is currently hidden, remove it from hidden list
      setLocalTripFilter((prev) => ({
        ...prev,
        hiddenTripIds: prev.hiddenTripIds.filter((id) => id !== tripId),
      }));
    } else {
      // Trip is currently visible, add it to hidden list
      setLocalTripFilter((prev) => ({
        ...prev,
        hiddenTripIds: [...prev.hiddenTripIds, tripId],
      }));
    }
  };

  const toggleSpecificTrip = (tripId: string) => {
    if (localTripFilter.visibleTripIds.includes(tripId)) {
      // Trip is explicitly included, remove it
      setLocalTripFilter((prev) => ({
        ...prev,
        visibleTripIds: prev.visibleTripIds.filter((id) => id !== tripId),
      }));
    } else {
      // Trip is not included, add it
      setLocalTripFilter((prev) => ({
        ...prev,
        visibleTripIds: [...prev.visibleTripIds, tripId],
      }));
    }
  };

  const handleApplyFilters = () => {
    onFilterChange(localTripFilter);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilter = {
      showAll: true,
      visibleTripIds: [],
      hiddenTripIds: [],
    };
    setLocalTripFilter(resetFilter);
    onFilterChange(resetFilter);
    onClose();
  };

  // Check if a trip is currently visible based on filter settings
  const isTripVisible = (tripId: string) => {
    if (!localTripFilter.showAll) {
      // When not showing all, only trips in visibleTripIds are shown
      return localTripFilter.visibleTripIds.includes(tripId);
    } else {
      // When showing all, trips in hiddenTripIds are hidden
      return !localTripFilter.hiddenTripIds.includes(tripId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Filter Trips</DialogTitle>
          <DialogDescription>
            Show or hide specific trips on the map.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center space-x-2 py-4">
          <Switch
            id="show-all-trips"
            checked={localTripFilter.showAll}
            onCheckedChange={toggleShowAll}
          />
          <Label htmlFor="show-all-trips">Show all trips</Label>
        </div>

        <div className="space-y-4">
          {!localTripFilter.showAll && (
            <div className="font-medium text-sm mb-2">
              Choose specific trips to display:
            </div>
          )}
          {localTripFilter.showAll && (
            <div className="font-medium text-sm mb-2">
              Uncheck trips you want to hide:
            </div>
          )}

          <ScrollArea className="h-[200px] border rounded-md p-2">
            {trips.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No trips available
              </div>
            ) : (
              <div className="space-y-2">
                {trips.map((trip) => (
                  <div key={trip.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`trip-${trip.id}`}
                      checked={isTripVisible(trip.id)}
                      onCheckedChange={() =>
                        localTripFilter.showAll
                          ? toggleTripVisibility(trip.id)
                          : toggleSpecificTrip(trip.id)
                      }
                    />
                    <Label
                      htmlFor={`trip-${trip.id}`}
                      className="flex items-center cursor-pointer text-sm"
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: trip.color ?? "#3B82F6" }}
                      />
                      Trip #{trip.tripNumber}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="outline" onClick={handleResetFilters}>
            Reset All
          </Button>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
